"use client"

import Link from "next/link"
import { CalendarRange, ArrowRight } from "lucide-react"
import { differenceInCalendarDays, format, parseISO } from "date-fns"
import { cn } from "@/lib/utils"
import { interactiveSurface, surface, sectionLabel } from "@/lib/dashboard-ui"
import { Skeleton } from "@/components/ui/skeleton"
import type { StudyPlan } from "@/services/planner"

interface StudyPlanCardProps {
    plan: StudyPlan | null
    loading?: boolean
}

/**
 * Exam countdown driven by the student's own study plan.
 *
 * The previous dashboard computed `daysLeft` against a hard-coded
 * `new Date('2026-03-14')` — a date that has since passed, so the value was
 * negative — and then never rendered it. `plannerService.getMyPlan()` already
 * holds the real per-user `exam_date` and `target_score`, so use that.
 */
export function StudyPlanCard({ plan, loading }: StudyPlanCardProps) {
    if (loading) {
        return (
            <section>
                <Skeleton className="mb-3 h-4 w-24" />
                <div className={cn(surface, "space-y-3 p-5")}>
                    <Skeleton className="h-10 w-24" />
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-2 w-full" />
                </div>
            </section>
        )
    }

    // No plan yet — the useful thing to show is how to make one.
    if (!plan) {
        return (
            <section aria-labelledby="plan-heading">
                <h2 id="plan-heading" className={cn(sectionLabel, "mb-3")}>
                    Study plan
                </h2>
                <Link href="/dashboard/planner" className={cn(interactiveSurface, "group flex items-center gap-4 p-5")}>
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <CalendarRange className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block text-base font-bold leading-tight text-foreground">
                            Build your study plan
                        </span>
                        <span className="mt-0.5 block text-sm text-muted-foreground">
                            Set your exam date and target score
                        </span>
                    </span>
                    <ArrowRight
                        className="h-5 w-5 shrink-0 text-muted-foreground motion-safe:transition-transform motion-safe:duration-200 motion-safe:group-hover:translate-x-1"
                        aria-hidden="true"
                    />
                </Link>
            </section>
        )
    }

    const examDate = parseISO(plan.exam_date)
    const daysLeft = differenceInCalendarDays(examDate, new Date())
    const done = plan.stats?.completed_tasks ?? 0
    const total = plan.stats?.total_tasks ?? 0
    const pct = total > 0 ? Math.round((done / total) * 100) : 0

    // Urgency is carried by the words ("2 weeks out"), not by colour alone.
    const urgency =
        daysLeft < 0 ? "Exam date has passed"
        : daysLeft === 0 ? "Exam is today"
        : daysLeft === 1 ? "Exam is tomorrow"
        : `until ${format(examDate, "MMM d, yyyy")}`

    return (
        <section aria-labelledby="plan-heading">
            <h2 id="plan-heading" className={cn(sectionLabel, "mb-3")}>
                Study plan
            </h2>
            <Link href="/dashboard/planner" className={cn(interactiveSurface, "group block p-5")}>
                <div className="flex items-baseline gap-2">
                    {daysLeft > 0 ? (
                        <>
                            <span className="text-4xl font-bold leading-none tracking-tight text-foreground tabular-nums">
                                {daysLeft}
                            </span>
                            <span className="text-base font-semibold text-muted-foreground">
                                {daysLeft === 1 ? "day" : "days"}
                            </span>
                        </>
                    ) : (
                        <span className="text-2xl font-bold leading-tight text-foreground">{urgency}</span>
                    )}
                </div>
                {daysLeft > 0 && <p className="mt-1 text-sm text-muted-foreground">{urgency}</p>}

                {total > 0 && (
                    <div className="mt-4">
                        <div className="mb-1.5 flex items-baseline justify-between text-sm">
                            <span className="font-medium text-foreground">Plan progress</span>
                            <span className="text-muted-foreground tabular-nums">
                                {done} of {total} tasks
                            </span>
                        </div>
                        <div
                            role="progressbar"
                            aria-valuenow={pct}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`Study plan progress: ${done} of ${total} tasks complete`}
                            className="h-2 w-full overflow-hidden rounded-full bg-muted"
                        >
                            <div
                                className="h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-700"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                    </div>
                )}

                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                    Open planner
                    <ArrowRight
                        className="h-4 w-4 motion-safe:transition-transform motion-safe:duration-200 motion-safe:group-hover:translate-x-1"
                        aria-hidden="true"
                    />
                </span>
            </Link>
        </section>
    )
}
